import { create } from 'zustand';
import { apiService } from '@/lib/api-functions/api-service';
import { clearUserData } from '@/lib/stores/unifiedUserStore';
import { clearLastOnlineTracking } from '@/lib/utils/lastOnlineTracker';
import { supabase } from '@/lib/config/supabase';

export type UserRole = 'owner' | 'contributor';

export interface User {
  id: string;
  email: string;
  user_metadata?: {
    username?: string;
    avatar_url?: string;
  };
}

export interface Session {
  user: User;
  // Add other session properties as needed
}

export interface UserProfile {
  id: string;
  avatar_url?: string;
  full_name?: string;
  settings?: Json;
  Last_Online?: string;
  email: string;
  username?: string;
  bio?: string;
  role: UserRole;
  created_at?: string;
  updated_at?: string;
}

type Json = Record<string, unknown>; // Settings object type

export interface GameProject {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  banner_url?: string;
  owner_id: string;
  is_live?: boolean;
  settings?: Json;
  created_at?: string;
  updated_at?: string;
  translation_deadline?: string;
  languages?: Array<{
    code: string;
    name: string;
    required: boolean;
  }>;
  defaultLanguage?: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  games: GameProject[];
  selectedGameId?: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error?: string }>;
  signUp: (email: string, password: string) => Promise<{ error?: string }>;
  signUpWithMagicLink: (email: string, invitationCode?: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  selectGame: (gameId: string) => void;
  fetchProfile: () => Promise<void>;
  fetchGames: () => Promise<void>;
  createGame: (gameData: {
    name: string;
    description?: string;
    is_live: boolean;
    banner_url?: string;
    settings?: Json;
  }) => Promise<{ data?: GameProject; error?: string }>;
  updateGame: (gameId: string, updates: Partial<GameProject>) => Promise<{ error?: string }>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  games: [],
  selectedGameId: undefined,
  isAuthenticated: false,
  isLoading: true,

  signIn: async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await apiService.login(email, password);
      if (!response.success || !response.data) {
        return { error: response.error || 'Login failed' };
      }
      const { user, token } = response.data;
      // Store the token with persistence based on rememberMe
      apiService.storeToken(token, rememberMe);
      
      // Create user and profile objects from the complete profile data
      const userObj: User = {
        id: user.id,
        email: user.email,
        user_metadata: {
          username: user.username,
          avatar_url: user.avatar_url
        }
      };

      const profileObj: UserProfile = {
        id: user.id,
        email: user.email,
        username: user.username,
        avatar_url: user.avatar_url,
        full_name: user.full_name,
        settings: user.settings,
        Last_Online: user.Last_Online,
        role: 'contributor', // Default role from login
        created_at: user.created_at,
        updated_at: user.updated_at
      };
      
      // Update state with both user and profile
      set({
        user: userObj,
        profile: profileObj,
        isAuthenticated: true,
        isLoading: false,
      });

      // User data will be initialized by SimplifiedUserDataProvider
      
      return {};
    } catch (error) {
      console.error("Auth store: Unexpected error:", error);
      return { error: 'An unexpected error occurred' };
    }
  },

  signUp: async (email: string, password: string) => {
    try {
      // Use API instead of Supabase
      const response = await apiService.signUp(email, password);
      if (!response.success) {
        return { error: response.error || 'Sign up failed' };
      }
      return {};
    } catch (error) {
      console.error("Auth store: Unexpected error:", error);
      return { error: 'An unexpected error occurred' };
    }
  },

  signUpWithMagicLink: async (email: string, invitationCode?: string) => {
    try {
      // Redirect to /login for token verification, then /login will redirect to /finish-profile
      const redirectUrl = 'https://app.locanest.app/login';

      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl,
          data: invitationCode ? {
            invitation_code: invitationCode,
            invited_email: email
          } : {
            invited_email: email
          }
        }
      });

      if (error) {
        console.error("Magic link signup error:", error);
        return { error: error.message || 'Failed to send magic link' };
      }

      return {};
    } catch (error) {
      console.error("Auth store: Unexpected error during magic link signup:", error);
      return { error: 'An unexpected error occurred' };
    }
  },

  signOut: async () => {
    // Call API logout
    await apiService.logout();

    // Clear unified user data cache
    await clearUserData();

    // Clear Last_Online tracking
    clearLastOnlineTracking();

    // Clear local state
    set({
      user: null,
      session: null,
      profile: null,
      games: [],
      selectedGameId: undefined,
      isAuthenticated: false,
    });
  },

  selectGame: (gameId: string) => {
    set({ selectedGameId: gameId });
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user) return;

    try {
      // Use API 
      const response = await apiService.fetchProfile();
      if (!response.success || !response.data) {
        console.error('Error fetching profile:', response.error);
        return;
      }

      // Access the nested user object from the response
      const userData = response.data.user;
      
      const profile: UserProfile = {
        id: (userData as any).id as string,
        email: (userData as any).email as string,
        username: (userData as any).username as string,
        avatar_url: (userData as any).avatar_url as string,
        role: (userData as any).role as UserRole,
        created_at: (userData as any).created_at as string,
        updated_at: (userData as any).updated_at as string,
      };

      set({ profile });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  },

  fetchGames: async () => {
    const { profile } = get();
    if (!profile || profile.role !== 'owner') return;

    try {
      // Use API instead of Supabase
      const response = await apiService.fetchGames();
      if (!response.success || !response.data) {
        console.error('Error fetching games:', response.error);
        return;
      }

      set({ 
        games: response.data || [],
        selectedGameId: response.data && response.data.length > 0 ? response.data[0].id : undefined
      });
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  },

 

  createGame: async (gameData) => {
    /*  const { profile } = get();
     if (!profile || profile.role !== 'owner') {
       return { error: 'Only owners can create games' };
     }
 
     try {
       const game = await apiService.createGame(profile.id, gameData);
       if (game) {
         await get().fetchGames();
         return { data: game };
       }
       return { error: 'Failed to create game' };
     } catch (error) {
       return { error: 'An unexpected error occurred' };
     } */
    return { error: 'Create game not implemented' };
  },

  updateGame: async (gameId, updates) => {
   /*  const { profile } = get();
    if (!profile || profile.role !== 'owner') {
      return { error: 'Only owners can update games' };
    }

    try {
      const updatedGame = await apiService.updateGame(gameId, updates);
      if (updatedGame) {
        await get().fetchGames();
        return {};
      }
      return { error: 'Failed to update game' };
    } catch (error) {
      return { error: 'An unexpected error occurred' };
    } */
    return { error: 'Update game not implemented' };
  },
}));

// Initialize auth state on app startup
export const initializeAuth = () => {
  const store = useAuthStore.getState();

  // Check for existing token and validate it
  const token = apiService.getToken();
  if (token) {
    // Check if user used "keep me logged in" (token in localStorage)
    const isRememberMe = localStorage.getItem('auth_token') !== null;
    
    // Check if token has less than 10 hours remaining
    const tokenInfo = apiService.getTokenInfo();
    const shouldRefreshImmediately = isRememberMe && tokenInfo && !tokenInfo.isExpired && checkIfLessThan10Hours(tokenInfo.timeUntilExpiration);
    
    if (shouldRefreshImmediately) {
      console.log('Auth: Token has less than 10 hours remaining (' + tokenInfo.timeUntilExpiration + '), refreshing immediately to prevent work loss');
      // Trigger immediate refresh
      apiService.refreshToken().then(response => {
        if (response.success && response.data?.token) {
          // Store the new token, maintaining the rememberMe preference
          apiService.storeToken(response.data.token, isRememberMe);
          console.log('Auth: Token refreshed successfully on startup');
        } else {
          console.error('Auth: Failed to refresh token on startup:', response.error);
        }
      }).catch(error => {
        console.error('Auth: Failed to refresh token on startup:', error);
        // Continue with normal flow even if refresh fails
      });
    }
    
    // Validate token with API
    apiService.validateToken().then(response => {
      if (response.success && response.data) {
        const user = response.data.user;
        const userObj = {
          id: user.id,
          email: user.email,
          user_metadata: {
            username: user.username,
            avatar_url: user.avatar_url,
          },
        } as unknown as User;
        
        useAuthStore.setState({
          user: userObj,
          isAuthenticated: true,
          isLoading: false,
        });
        
        // Initialize token refresh service with the existing token
        // This will schedule automatic refresh before expiration
        const currentToken = apiService.getToken();
        if (currentToken) {
          const tokenInfo = apiService.getTokenInfo();
          if (tokenInfo) {
            console.log('Auth: Token refresh scheduled - expires in', tokenInfo.timeUntilExpiration);
          }
        }
        
        // User data will be initialized by SimplifiedUserDataProvider
      } else {
        // Invalid token (including 401), clear it silently
        // The token may have already been cleared by the 401 handler in api-service
        apiService.clearToken();
        useAuthStore.setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    }).catch(() => {
      // Network error or other issue - clear auth silently
      apiService.clearToken();
      useAuthStore.setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    });
  } else {
    useAuthStore.setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }
};

/**
 * Helper function to check if time remaining is less than 10 hours
 * Parses time strings like "23h 45m" or "9h 30m" or "5m 30s"
 */
function checkIfLessThan10Hours(timeString: string): boolean {
  const hourMatch = timeString.match(/(\d+)h/);
  const minuteMatch = timeString.match(/(\d+)m/);
  
  const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
  const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
  
  // Convert to total minutes
  const totalMinutes = hours * 60 + minutes;
  
  // 10 hours = 600 minutes
  return totalMinutes < 600;
}

